import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, X, Check, Link2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
export const absMediaUrl = (u) => (!u ? "" : u.startsWith("http") || u.startsWith("data:") ? u : `${BACKEND}${u}`);

/**
 * Reusable image field: upload a new file OR pick from the media library
 * (restricted to the current user's own uploads; admins can toggle "All media").
 * Stores an absolute URL string via onChange.
 */
export default function MediaPicker({ value, onChange, kind = "media_library", testid = "media", showUrlField = true }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(absMediaUrl(data.url));
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid={`${testid}-picker`}>
      <div className="flex items-center gap-3">
        <div className="h-16 w-16 shrink-0 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
          {value ? (
            <img src={absMediaUrl(value)} alt="" className="h-full w-full object-cover" data-testid={`${testid}-preview`} />
          ) : (
            <ImageIcon className="h-5 w-5 text-slate-400" />
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} data-testid={`${testid}-file`} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 rounded-full bg-[#165a71] text-white px-3 py-1.5 text-xs font-medium disabled:opacity-50 hover:bg-[#11485a] transition-colors" data-testid={`${testid}-upload-btn`}>
            <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload"}
          </button>
          <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium hover:border-[#165a71]/40 transition-colors" data-testid={`${testid}-library-btn`}>
            <ImageIcon className="h-3.5 w-3.5" /> Library
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 transition-colors" data-testid={`${testid}-clear-btn`}>
              <X className="h-3.5 w-3.5" /> Clear
            </button>
          )}
        </div>
      </div>
      {showUrlField && (
        <div className="mt-2 flex items-center gap-2">
          <Link2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste an image URL" className="flex-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs outline-none focus:border-[#165a71]" data-testid={`${testid}-url`} />
        </div>
      )}
      {open && <LibraryModal kind={kind} onClose={() => setOpen(false)} onPick={(u) => { onChange(u); setOpen(false); }} testid={testid} />}
    </div>
  );
}

function LibraryModal({ onClose, onPick, testid }) {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).some((r) => ["administrator", "super_administrator", "moderator"].includes(r));
  const [scope, setScope] = useState("mine");
  const [items, setItems] = useState(null);
  useEffect(() => {
    setItems(null);
    api.get("/files/library", { params: { scope } })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, [scope]);
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#071b22]/50 p-4" data-testid={`${testid}-library-modal`} onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[80vh] overflow-hidden rounded-3xl bg-white shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-100 flex items-center gap-3">
          <div className="font-display text-lg font-semibold text-[#071b22]">Choose from library</div>
          {isAdmin && (
            <div className="ml-2 inline-flex rounded-full border border-slate-200 p-0.5 text-xs">
              <button onClick={() => setScope("mine")} className={`px-3 py-1 rounded-full ${scope === "mine" ? "bg-[#165a71] text-white" : "text-[#252A34]"}`} data-testid={`${testid}-scope-mine`}>My uploads</button>
              <button onClick={() => setScope("all")} className={`px-3 py-1 rounded-full ${scope === "all" ? "bg-[#165a71] text-white" : "text-[#252A34]"}`} data-testid={`${testid}-scope-all`}>All media</button>
            </div>
          )}
          <button onClick={onClose} className="ml-auto rounded-full p-1.5 hover:bg-slate-100" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto">
          {items === null ? (
            <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">No images yet. Use <strong>Upload</strong> to add one.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {items.map((m) => (
                <button key={m.id} type="button" onClick={() => onPick(m.url)} className="group relative aspect-square rounded-xl border border-slate-200 overflow-hidden hover:border-[#165a71] transition-colors" data-testid={`${testid}-lib-item-${m.id}`} title={m.filename}>
                  <img src={absMediaUrl(m.url)} alt={m.filename} className="h-full w-full object-cover" />
                  <span className="absolute inset-0 bg-[#165a71]/0 group-hover:bg-[#165a71]/25 flex items-center justify-center transition-colors">
                    <Check className="h-6 w-6 text-white opacity-0 group-hover:opacity-100" />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
